import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";

import {
  getById as getAppointmentById,
  sendDepositPix,
  type DepositPixChannel,
} from "../../services/appointmentService";
import { getClientById } from "../../services/clientService";
import {
  getMessagingSettings,
} from "../../services/messagingSettingsService";
import { getById as getOrderById } from "../../services/orderService";

type PixPaymentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  appointmentId?: number | null;
  orderId?: number | null;
  qrCodeBase64: string;
  copyPasteString: string;
  onPaymentConfirmed?: () => void;
};

const POLL_INTERVAL_MS = 3000;

const PixPaymentModal = ({
  isOpen,
  onClose,
  appointmentId = null,
  orderId = null,
  qrCodeBase64,
  copyPasteString,
  onPaymentConfirmed,
}: PixPaymentModalProps) => {
  const { t } = useTranslation();
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [polling, setPolling] = useState(false);
  const [sendingChannel, setSendingChannel] = useState<DepositPixChannel | null>(null);
  const [sendReadinessLoading, setSendReadinessLoading] = useState(false);
  const [clientHasEmail, setClientHasEmail] = useState(false);
  const [clientHasPhone, setClientHasPhone] = useState(false);
  const [messagingOutboundEnabled, setMessagingOutboundEnabled] = useState(false);
  const confirmedRef = useRef(false);
  const isOrderFlow = orderId !== null;
  const canSendDepositPix = !isOrderFlow && appointmentId !== null;
  const emailDisabledReason = !clientHasEmail ? t("agenda.pix.sendEmailDisabledHint") : null;
  const whatsappDisabledReason = !messagingOutboundEnabled
    ? t("agenda.pix.sendWhatsappDisabledHint")
    : !clientHasPhone
      ? t("agenda.pix.sendWhatsappNoPhoneHint")
      : null;
  const emailSendDisabled = sendingChannel !== null || sendReadinessLoading || !clientHasEmail;
  const whatsappSendDisabled =
    sendingChannel !== null
    || sendReadinessLoading
    || !clientHasPhone
    || !messagingOutboundEnabled;

  useEffect(() => {
    if (!isOpen) {
      setPaymentConfirmed(false);
      setPolling(false);
      setSendingChannel(null);
      setSendReadinessLoading(false);
      setClientHasEmail(false);
      setClientHasPhone(false);
      setMessagingOutboundEnabled(false);
      confirmedRef.current = false;
      return;
    }

    if (appointmentId === null && orderId === null) {
      return;
    }

    let cancelled = false;
    setPolling(true);

    const pollStatus = async () => {
      try {
        if (orderId !== null) {
          const response = await getOrderById(orderId);

          if (!cancelled && !confirmedRef.current && response.data.status === "paid") {
            confirmedRef.current = true;
            setPaymentConfirmed(true);
            setPolling(false);
            toast.success(t("studio.orders.checkout.payment.pix.paymentConfirmedToast"));
            onPaymentConfirmed?.();
          }

          return;
        }

        if (appointmentId !== null) {
          const response = await getAppointmentById(appointmentId);
          const appointment = response.data;

          if (
            !cancelled
            && !confirmedRef.current
            && (appointment.deposit_status === "paid" || appointment.status === "confirmed")
          ) {
            confirmedRef.current = true;
            setPaymentConfirmed(true);
            setPolling(false);
            toast.success(t("agenda.pix.paymentConfirmedToast"));
            onPaymentConfirmed?.();
          }
        }
      } catch {
        // keep polling — transient network errors should not stop monitoring
      }
    };

    void pollStatus();
    const intervalId = window.setInterval(() => {
      if (!confirmedRef.current) {
        void pollStatus();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [appointmentId, isOpen, onPaymentConfirmed, orderId, t]);

  useEffect(() => {
    if (!isOpen || appointmentId === null || isOrderFlow) {
      return;
    }

    let cancelled = false;
    setSendReadinessLoading(true);

    const loadSendReadiness = async () => {
      try {
        const [appointmentResponse, messagingResponse] = await Promise.all([
          getAppointmentById(appointmentId),
          getMessagingSettings(),
        ]);

        const clientId = appointmentResponse.data.client_id;
        const clientResponse = await getClientById(clientId);

        if (cancelled) {
          return;
        }

        setClientHasEmail(Boolean(clientResponse.data.email?.trim()));
        setClientHasPhone(Boolean(clientResponse.data.phone_number?.trim()));
        setMessagingOutboundEnabled(
          Boolean(messagingResponse.data.health?.iugis_messaging_active)
        );
      } catch {
        if (!cancelled) {
          setClientHasEmail(false);
          setClientHasPhone(false);
          setMessagingOutboundEnabled(false);
        }
      } finally {
        if (!cancelled) {
          setSendReadinessLoading(false);
        }
      }
    };

    void loadSendReadiness();

    return () => {
      cancelled = true;
    };
  }, [appointmentId, isOpen, isOrderFlow]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyPasteString);
      toast.success(t("agenda.pix.copiedSuccess"));
    } catch {
      return;
    }
  };

  const handleSendDepositPix = async (channel: DepositPixChannel) => {
    if (appointmentId === null || sendingChannel !== null) {
      return;
    }

    if (channel === "email" && emailSendDisabled) {
      toast.error(emailDisabledReason || t("agenda.pix.sendFailed"));
      return;
    }

    if (channel === "whatsapp" && whatsappSendDisabled) {
      toast.error(whatsappDisabledReason || t("agenda.pix.sendFailed"));
      return;
    }

    setSendingChannel(channel);

    try {
      const response = await sendDepositPix(appointmentId, { channels: [channel] });
      const channelResult = response.data.channels[channel];

      if (channelResult?.status === "queued") {
        toast.success(
          channel === "email"
            ? t("agenda.pix.sendEmailSuccess")
            : t("agenda.pix.sendWhatsappSuccess")
        );
        return;
      }

      toast.error(
        channelResult?.message
          || t("agenda.pix.sendFailed")
      );
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError?.data || t("agenda.pix.sendFailed"));
    } finally {
      setSendingChannel(null);
    }
  };

  const modalTitle = isOrderFlow
    ? t("studio.orders.checkout.payment.pix.modalTitle")
    : t("agenda.pix.modalTitle");

  const paymentConfirmedMessage = isOrderFlow
    ? t("studio.orders.checkout.payment.pix.paymentConfirmed")
    : t("agenda.pix.paymentConfirmed");

  const awaitingMonitoringMessage = isOrderFlow
    ? t("studio.orders.checkout.payment.pix.awaitingPaymentMonitoring")
    : t("agenda.pix.awaitingPaymentMonitoring");

  const awaitingPaymentMessage = isOrderFlow
    ? t("studio.orders.checkout.payment.awaitingPayment")
    : t("agenda.pix.awaitingPayment");

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="md">
      <ModalHeader toggle={onClose}>{modalTitle}</ModalHeader>
      <ModalBody>
        {paymentConfirmed ? (
          <Alert color="success" className="mb-0">
            <i className="ri-checkbox-circle-line me-2" />
            {paymentConfirmedMessage}
          </Alert>
        ) : (
          <>
            <div className="text-center mb-4">
              <img
                src={`data:image/png;base64,${qrCodeBase64}`}
                alt=""
                className="img-fluid border rounded p-2 bg-white"
                style={{ maxWidth: "220px" }}
              />
            </div>

            <Label htmlFor="pix-copy-paste">{t("agenda.pix.copyPasteLabel")}</Label>
            <InputGroup>
              <Input id="pix-copy-paste" type="text" readOnly value={copyPasteString} />
              <Button
                color="primary"
                type="button"
                onClick={handleCopy}
                aria-label={t("agenda.pix.copyPasteLabel")}
              >
                <i className="ri-file-copy-line" />
              </Button>
            </InputGroup>

            <Alert color="info" className="mt-3 mb-0">
              {polling ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {awaitingMonitoringMessage}
                </>
              ) : (
                awaitingPaymentMessage
              )}
            </Alert>

            {canSendDepositPix && (
              <div className="mt-3">
                <p className="text-muted small mb-2">{t("agenda.pix.sendHint")}</p>
                {sendReadinessLoading ? (
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <Spinner size="sm" />
                    {t("agenda.pix.sendReadinessLoading")}
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    <span id="pix-send-email-wrap">
                      <Button
                        color="soft-success"
                        type="button"
                        disabled={emailSendDisabled}
                        onClick={() => void handleSendDepositPix("email")}
                      >
                        {sendingChannel === "email" ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="ri-mail-send-line me-1" />
                        )}
                        {t("agenda.pix.sendEmail")}
                      </Button>
                    </span>
                    {emailDisabledReason ? (
                      <UncontrolledTooltip target="pix-send-email-wrap" placement="top">
                        {emailDisabledReason}
                      </UncontrolledTooltip>
                    ) : null}
                    <span id="pix-send-whatsapp-wrap">
                      <Button
                        color="soft-success"
                        type="button"
                        disabled={whatsappSendDisabled}
                        onClick={() => void handleSendDepositPix("whatsapp")}
                      >
                        {sendingChannel === "whatsapp" ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="ri-whatsapp-line me-1" />
                        )}
                        {t("agenda.pix.sendWhatsapp")}
                      </Button>
                    </span>
                    {whatsappDisabledReason ? (
                      <UncontrolledTooltip target="pix-send-whatsapp-wrap" placement="top">
                        {whatsappDisabledReason}
                      </UncontrolledTooltip>
                    ) : null}
                  </div>
                )}
                {(emailDisabledReason || whatsappDisabledReason) && !sendReadinessLoading ? (
                  <p className="text-muted small mt-2 mb-0">
                    {[emailDisabledReason, whatsappDisabledReason].filter(Boolean).join(" ")}
                  </p>
                ) : null}
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color={paymentConfirmed ? "success" : "light"} onClick={onClose}>
          {paymentConfirmed ? t("agenda.pix.closeConfirmed") : t("studio.agenda.actions.cancel")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PixPaymentModal;
